import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Truck,
  Shield,
  MapPin,
  Sparkles,
  Database,
  CheckCircle2,
  Clock,
  Activity,
  FileCheck,
  Users,
  Lock,
} from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useSEO, faqPageJsonLd, siteUrl, videoObjectJsonLd } from "@/lib/seo";
import { serviceList } from "@/data/services";
import { locationList } from "@/data/locations";
import { fetchFAQs, fetchBlog } from "@/lib/api";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";
import VideoFeature from "@/components/VideoFeature";
import GoogleReviews, { reviewsAggregateJsonLd } from "@/components/GoogleReviews";

const HERO_IMAGE = "/images/hero-wasz-fleet.jpg";

const FEATURE_VIDEO = {
  id: "Z0amOxxHmDM",
  uploadDate: "2017-04-15",
  thumbnail: "https://i.ytimg.com/vi/Z0amOxxHmDM/maxresdefault.jpg",
  watchUrl: "https://www.youtube.com/watch?v=Z0amOxxHmDM",
  embedUrl: "https://www.youtube-nocookie.com/embed/Z0amOxxHmDM",
};

const StatCard = ({ value, label, dataTestid }) => (
  <div data-testid={dataTestid} className="border-l border-white/10 pl-5 py-2">
    <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 mt-1 font-medium">{label}</div>
  </div>
);

const ServiceTile = ({ to, title, blurb, icon: Icon, image, large, dataTestid, viewLabel }) => (
  <Link
    to={to}
    data-testid={dataTestid}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0F0F23] card-hover ${large ? "row-span-2" : ""}`}
  >
    {image && (
      <div className="absolute inset-0 -z-0">
        <img src={image} alt={title} className="w-full h-full object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F23] via-[#0F0F23]/85 to-[#0F0F23]/55" />
      </div>
    )}
    <div className="relative p-7 sm:p-8 h-full flex flex-col">
      <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/20 flex items-center justify-center mb-5">
        <Icon className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.8} />
      </div>
      <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-3">{title}</h3>
      <p className="text-white/55 text-sm leading-relaxed flex-1">{blurb}</p>
      <div className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#00D4FF]">
        <span>{viewLabel}</span>
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
      </div>
    </div>
  </Link>
);

const Home = () => {
  const { t, lang } = useLang();
  const [faqs, setFaqs] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    fetchFAQs().then(setFaqs).catch(() => {});
    fetchBlog().then((p) => setLatestPosts((p || []).slice(0, 4))).catch(() => {});
  }, []);

  useSEO({
    title: lang === "nl"
      ? "Wasz.nl — Mobiele Truckwash & Digitale Wagenparkadministratie"
      : "Wasz.nl — Mobile Truckwash & Digital Fleet Administration",
    description: lang === "nl"
      ? "Wasz.nl reinigt vrachtwagens en bedrijfsvloten op locatie en levert het complete digitale dossier via WaszBase: reinigingsregistratie, NVWA-documentatie en facturatie per wasdag."
      : "Wasz.nl cleans trucks and corporate fleets on-site and delivers the full digital dossier through WaszBase: cleaning records, NVWA documentation and invoicing per wash-day.",
    canonical: siteUrl("/"),
    image: siteUrl("/og-image-ai.jpg?v=4"),
    lang,
    jsonLd: [
      {
        id: "home-video-jsonld",
        data: videoObjectJsonLd({
          name: lang === "nl"
            ? "Wasz.nl in Doe Maar Duurzaam — Mobiele truckwash voor transport-Nederland"
            : "Wasz.nl on Doe Maar Duurzaam — Mobile truckwash for transport Netherlands",
          description: lang === "nl"
            ? "Wasz.nl te gast in het AVROTROS- en RTL-Z-programma Doe Maar Duurzaam. Hoe mobiele truckwash transportbedrijven helpt vlootstilstand te voorkomen, water gecontroleerd af te voeren en duurzamer te ondernemen."
            : "Wasz.nl featured on the Dutch broadcasters AVROTROS and RTL-Z, on the programme Doe Maar Duurzaam. How mobile truckwash helps transport companies eliminate fleet downtime, manage wastewater responsibly and operate more sustainably.",
          thumbnailUrl: FEATURE_VIDEO.thumbnail,
          uploadDate: FEATURE_VIDEO.uploadDate,
          contentUrl: FEATURE_VIDEO.watchUrl,
          embedUrl: FEATURE_VIDEO.embedUrl,
        }),
      },
      ...(faqs.length
        ? [{ id: "home-faq-jsonld", data: faqPageJsonLd(faqs.slice(0, 6), lang) }]
        : []),
      { id: "home-reviews-jsonld", data: reviewsAggregateJsonLd },
    ],
  });

  return (
    <main data-testid="home-page" className="min-h-screen">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt={lang === "nl" ? "Wasz.nl mobiele truckwash service-bus en wagenpark op locatie" : "Wasz.nl mobile truckwash service van and fleet on location"}
            className="w-full h-full object-cover object-center"
            fetchPriority="high"
          />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 hero-overlay-bottom" />
          <div className="absolute inset-0 cyber-grid opacity-15" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 w-full py-24">
          <div className="max-w-3xl reveal">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00D4FF]/25 bg-[#00D4FF]/5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00D4FF] opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00D4FF]"></span>
              </span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#00D4FF] font-semibold">
                {t("hero.overline")}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[1.05]">
              <span className="block">{t("hero.title_a")}</span>
              <span className="block gradient-text text-[clamp(1.75rem,4vw,3.25rem)]">{t("hero.title_b")}</span>
            </h1>

            <p className="mt-7 text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contact?type=quote" data-testid="hero-cta-quote">
                <button className="btn-primary">
                  {t("hero.cta_primary")} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/waszbase" data-testid="hero-cta-demo">
                <button className="btn-secondary">
                  <Sparkles className="w-4 h-4" /> {t("hero.cta_demo")}
                </button>
              </Link>
              <a
                href="https://www.waszbase.co.nl/klant/login"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-klant-login"
              >
                <button className="btn-secondary">
                  <Lock className="w-4 h-4" /> Klant Login
                </button>
              </a>
              <a href="tel:0365255021" data-testid="hero-cta-call" className="text-sm text-white/60 hover:text-[#00D4FF] inline-flex items-center gap-2 ml-2">
                <Phone className="w-4 h-4" /> 036 525 5021
              </a>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-6">
              <StatCard dataTestid="stat-trucks" value="1.570+" label={t("hero.stats_a")} />
              <StatCard dataTestid="stat-coverage" value="100%" label={t("hero.stats_b")} />
              <StatCard dataTestid="stat-haccp" value="HACCP" label={t("hero.stats_c")} />
              <StatCard dataTestid="stat-uptime" value="99.4%" label={t("hero.stats_d")} />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-10 border-y border-white/5 bg-[#0a0a18]/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-x-12 gap-y-6 text-white/45">
            <div className="text-[11px] uppercase tracking-[0.22em] font-medium">
              {lang === "nl" ? "Vertrouwd door transport-Nederland" : "Trusted across transport NL"}
            </div>
            <div className="flex flex-wrap items-center gap-x-10 gap-y-3 text-xs font-semibold tracking-wider uppercase">
              <span className="hover:text-white transition-colors">HACCP</span>
              <span className="hover:text-white transition-colors">{lang === "nl" ? "NVWA conform" : "NVWA compliant"}</span>
              <span className="hover:text-white transition-colors">{lang === "nl" ? "ISO-georiënteerd" : "ISO-oriented"}</span>
              <span className="hover:text-white transition-colors">{lang === "nl" ? "Bio-afbreekbaar" : "Biodegradable"}</span>
              <span className="hover:text-white transition-colors">{lang === "nl" ? "Landelijke dekking" : "Nationwide coverage"}</span>
              <span className="hover:text-white transition-colors">{lang === "nl" ? "B2B contracten" : "B2B contracts"}</span>
            </div>
          </div>
        </div>
      </section>

      {/* AS FEATURED ON — DOE MAAR DUURZAAM */}
      <section
        data-testid="home-video-section"
        id="doe-maar-duurzaam"
        className="py-24 sm:py-28 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a18] via-[#050509] to-[#050509] -z-10" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5">
              <p className="section-overline mb-3" data-testid="video-section-overline">
                {lang === "nl" ? "Op televisie · AVROTROS & RTL-Z" : "On television · AVROTROS & RTL-Z"}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1] mb-5">
                {lang === "nl" ? (
                  <>
                    Wasz.nl in <span className="text-[#00D4FF]">Doe Maar Duurzaam</span>.
                  </>
                ) : (
                  <>
                    Wasz.nl on <span className="text-[#00D4FF]">Doe Maar Duurzaam</span>.
                  </>
                )}
              </h2>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-5">
                {lang === "nl"
                  ? "In het programma Doe Maar Duurzaam — uitgezonden door AVROTROS en RTL-Z — liet Wasz.nl zien hoe mobiele truckwash transport-Nederland helpt om wagenpark-stilstand te voorkomen, water gecontroleerd af te voeren en duurzamer te ondernemen."
                  : "On the programme Doe Maar Duurzaam — broadcast by AVROTROS and RTL-Z — Wasz.nl demonstrated how mobile truckwash helps Dutch transport operators eliminate fleet downtime, manage wastewater responsibly and operate more sustainably."}
              </p>
              <p className="text-white/55 text-sm leading-relaxed">
                {lang === "nl"
                  ? "De principes uit deze uitzending — efficiënt op locatie reinigen, biologisch afbreekbare middelen en transparante registratie — vormen vandaag nog steeds de basis van onze dienstverlening en het WaszBase klantportaal."
                  : "The principles from this episode — efficient on-site cleaning, biodegradable agents and transparent record-keeping — still underpin our service and the WaszBase client portal today."}
              </p>
              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/40">
                <span>AVROTROS</span>
                <span>· RTL-Z</span>
                <span>· Doe Maar Duurzaam</span>
                <span>· {lang === "nl" ? "Sinds 2016" : "Since 2016"}</span>
              </div>
            </div>
            <div className="lg:col-span-7">
              <VideoFeature
                videoId={FEATURE_VIDEO.id}
                badge={lang === "nl" ? "Uitzending" : "Broadcast"}
                title={lang === "nl"
                  ? "Wasz.nl bij Doe Maar Duurzaam — mobiele truckwash & duurzaam wagenparkbeheer"
                  : "Wasz.nl on Doe Maar Duurzaam — mobile truckwash & sustainable fleet management"}
                description={lang === "nl"
                  ? "Reportage over duurzaam transport en mobiele vrachtwagenreiniging op locatie."
                  : "A reportage on sustainable transport and mobile on-site truck cleaning."}
                watchUrl={FEATURE_VIDEO.watchUrl}
                ariaLabel={lang === "nl"
                  ? "Speel video af: Wasz.nl in Doe Maar Duurzaam"
                  : "Play video: Wasz.nl on Doe Maar Duurzaam"}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 sm:py-32" id="services">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="section-overline mb-3">{lang === "nl" ? "Onze diensten" : "Our services"}</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {lang === "nl" ? (
                  <>Een volledige scope <span className="text-[#00D4FF]">truckwash & fleet cleaning</span>.</>
                ) : (
                  <>A complete <span className="text-[#00D4FF]">truckwash & fleet cleaning</span> scope.</>
                )}
              </h2>
            </div>
            <p className="text-white/55 text-sm sm:text-base max-w-md">
              {lang === "nl"
                ? "Van mobiele truckwash tot HACCP-conforme reiniging. Eén partner, vaste tarieven, één digitaal dossier per kenteken."
                : "From mobile truckwash to HACCP-compliant cleaning. One partner, fixed pricing, one digital dossier per license plate."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-5 lg:auto-rows-fr">
            <ServiceTile
              dataTestid="service-tile-mobiele-truckwash"
              to="/mobiele-truckwash"
              icon={Truck}
              image={services_image("mobiele-truckwash")}
              large
              viewLabel={lang === "nl" ? "Bekijk" : "View"}
              title={t("nav.mobile_truckwash")}
              blurb={lang === "nl" ? "Onze mobiele wasstraat komt naar uw locatie. Geen verloren rij-uren, geen wachttijd." : "Our mobile wash unit comes to you. No lost driving hours, no queues."}
            />
            <ServiceTile
              dataTestid="service-tile-vrachtwagenreiniging"
              to="/vrachtwagenreiniging"
              icon={Sparkles}
              image={services_image("vrachtwagenreiniging")}
              viewLabel={lang === "nl" ? "Bekijk" : "View"}
              title={t("nav.truck_cleaning")}
              blurb={lang === "nl" ? "Exterieur, interieur en HACCP-conforme reiniging voor levensmiddelentransport, certificaat inbegrepen." : "Exterior, interior and HACCP-compliant cleaning for food transport, certificate included."}
            />
            <ServiceTile
              dataTestid="service-tile-haccp"
              to="/haccp-certificaten"
              icon={Shield}
              image={services_image("haccp-certificaten")}
              viewLabel={lang === "nl" ? "Bekijk" : "View"}
              title={t("nav.haccp")}
              blurb={lang === "nl" ? "Compliance-ondersteunend. PDF-dossiers per kenteken, klaar om aan een controleur te tonen." : "Compliance-supporting. PDF dossiers per license plate, ready to present to an inspector."}
            />
            <ServiceTile
              dataTestid="service-tile-fleet-cleaning"
              to="/fleet-cleaning"
              icon={Activity}
              image={services_image("fleet-cleaning")}
              viewLabel={lang === "nl" ? "Bekijk" : "View"}
              title={t("nav.fleet_cleaning")}
              blurb={lang === "nl" ? "Structurele wascontracten met vaste cyclus, vaste prijs en maandelijkse prestatie-rapportage." : "Structural wash contracts with fixed cycles, fixed pricing and KPI reporting."}
            />
            <ServiceTile
              dataTestid="service-tile-wagenpark"
              to="/wagenpark-reiniging"
              icon={Users}
              image={services_image("wagenpark-reiniging")}
              viewLabel={lang === "nl" ? "Bekijk" : "View"}
              title={t("nav.wagenpark")}
              blurb={lang === "nl" ? "Eén contract voor uw hele vloot — van bestelbus tot zware trekker." : "One contract for your entire fleet — from van to heavy tractor."}
            />
          </div>
        </div>
      </section>

      {/* WASZBASE TEASER */}
      <section className="py-24 sm:py-32 bg-[#0a0a18] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="section-overline mb-3 inline-flex items-center gap-2">
                <Database className="w-3.5 h-3.5" /> WaszBase
              </p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {lang === "nl" ? (
                  <>Uw volledige wagenpark, <span className="text-[#00D4FF]">één klik</span> verwijderd.</>
                ) : (
                  <>Your entire fleet, <span className="text-[#00D4FF]">one click</span> away.</>
                )}
              </h2>
              <p className="mt-6 text-white/65 leading-relaxed">
                {lang === "nl"
                  ? "WaszBase is ons klantportaal voor digitale wagenparkadministratie. Kentekens, washistorie, HACCP-certificaten, NVWA-dossiers en facturen — alles per voertuig, in realtime."
                  : "WaszBase is our customer portal for digital fleet administration. License plates, wash history, HACCP certificates, NVWA dossiers and invoices — everything per vehicle, in real time."}
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  [FileCheck, lang === "nl" ? "Certificaatbeheer per kenteken" : "Certificate management per license plate"],
                  [Activity, lang === "nl" ? "Realtime washistorie & rapportage" : "Real-time wash history & reporting"],
                  [Users, lang === "nl" ? "Sub-klanten, kostenplaatsen en facturatie" : "Sub-customers, cost centers and billing"],
                  [Clock, lang === "nl" ? "Dossier binnen 30 seconden te tonen" : "Dossier shown within 30 seconds"],
                ].map(([Icon, text], i) => (
                  <li key={i} className="flex items-start gap-3 text-white/75">
                    <Icon className="w-5 h-5 text-[#00D4FF] mt-0.5 shrink-0" />
                    {text}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex gap-3">
                <a
                  href="https://www.waszbase.co.nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="waszbase-teaser-cta"
                >
                  <button className="btn-primary">
                    {lang === "nl" ? "Bekijk WaszBase" : "Explore WaszBase"} <ArrowRight className="w-4 h-4" />
                  </button>
                </a>
              </div>
            </div>

            {/* Dashboard preview */}
            <div className="relative">
              <div className="rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,212,255,0.18)]">
                <div className="px-5 py-3 border-b border-white/10 bg-[#050509] flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/70"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#facc15]/70"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/70"></span>
                  <span className="ml-3 text-[11px] mono text-white/40">waszbase.co.nl/dashboard</span>
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-white/40 mb-1">{lang === "nl" ? "Fleet overzicht" : "Fleet overview"}</div>
                      <div className="text-2xl font-bold text-white">{lang === "nl" ? "142 voertuigen" : "142 vehicles"}</div>
                    </div>
                    <div className="text-[11px] mono text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/30 px-2 py-1 rounded">{lang === "nl" ? "↑ 100% conform" : "↑ 100% compliant"}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {(lang === "nl"
                      ? [
                          { l: "Wasbeurten / mnd", v: "486" },
                          { l: "Certificaten actief", v: "139" },
                          { l: "NVWA-export", v: "3" },
                        ]
                      : [
                          { l: "Washes / month", v: "486" },
                          { l: "Active certificates", v: "139" },
                          { l: "NVWA export", v: "3" },
                        ]
                    ).map((s, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-[#050509] p-3">
                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{s.l}</div>
                        <div className="text-lg font-bold text-white">{s.v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[#050509] text-white/40 text-[10px] uppercase tracking-wider">
                        <tr>
                          <th className="text-left p-2.5">{lang === "nl" ? "Kenteken" : "License plate"}</th>
                          <th className="text-left p-2.5">{lang === "nl" ? "Laatste was" : "Last wash"}</th>
                          <th className="text-left p-2.5">HACCP</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/70">
                        {[
                          ["BX-04-NL", lang === "nl" ? "12 feb 2026" : "Feb 12, 2026", lang === "nl" ? "n.v.t." : "N/A"],
                          ["VR-89-DH", lang === "nl" ? "11 feb 2026" : "Feb 11, 2026", "OK"],
                          ["TR-12-AM", lang === "nl" ? "10 feb 2026" : "Feb 10, 2026", "OK"],
                          ["FL-77-RT", lang === "nl" ? "08 feb 2026" : "Feb 08, 2026", "PEND"],
                        ].map((r, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="p-2.5 mono text-[#00D4FF]">{r[0]}</td>
                            <td className="p-2.5">{r[1]}</td>
                            <td className="p-2.5">
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                                r[2] === "OK"
                                  ? "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30"
                                  : r[2] === "PEND"
                                  ? "bg-[#facc15]/10 text-[#facc15] border border-[#facc15]/30"
                                  : "bg-white/5 text-white/40 border border-white/10"
                              }`}>{r[2]}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="section-overline mb-3">{lang === "nl" ? "Lokale inzet" : "Local presence"}</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {lang === "nl" ? "Vijf kernregio's, één team." : "Five core regions, one team."}
              </h2>
              <p className="mt-4 text-white/55 text-base max-w-2xl">
                {lang === "nl" ? (
                  <>
                    Onze service-units rijden dagelijks vanuit onze thuisbasis <Link to="/truckwash-almere" className="text-[#00D4FF] hover:underline">Almere</Link> naar wagenparken in <Link to="/truckwash-amsterdam" className="text-[#00D4FF] hover:underline">Amsterdam</Link>, <Link to="/truckwash-utrecht" className="text-[#00D4FF] hover:underline">Utrecht</Link>, Rotterdam en Den Haag — en alle regio&apos;s daartussen.
                  </>
                ) : (
                  <>
                    Our service units run daily from our home base <Link to="/truckwash-almere" className="text-[#00D4FF] hover:underline">Almere</Link> to fleets in <Link to="/truckwash-amsterdam" className="text-[#00D4FF] hover:underline">Amsterdam</Link>, <Link to="/truckwash-utrecht" className="text-[#00D4FF] hover:underline">Utrecht</Link>, Rotterdam and The Hague — and every region in between.
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {locationList.map((loc) => {
              const c = loc[lang] || loc.nl;
              return (
                <Link
                  key={loc.slug}
                  to={`/truckwash-${loc.slug}`}
                  data-testid={`location-tile-${loc.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0F0F23] card-hover h-72 flex"
                >
                  <img src={loc.image} alt={`Truckwash ${loc.city}`} className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F23] via-[#0F0F23]/70 to-transparent" />
                  <div className="relative w-full p-5 flex flex-col">
                    <div className="mt-auto">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-[#00D4FF] font-semibold mb-2">
                        <MapPin className="w-3 h-3" /> {loc.region}
                      </div>
                      <div className="text-xl font-bold text-white leading-tight">{loc.city}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Extra service locaties — interne SEO link-strip */}
          <div className="mt-10 pt-8 border-t border-white/5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold mb-4">
              {lang === "nl" ? "Ook actief in" : "Also active in"}
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { slug: "amersfoort", city: "Amersfoort" },
                { slug: "alkmaar", city: "Alkmaar" },
                { slug: "alphen-aan-den-rijn", city: "Alphen aan den Rijn" },
                { slug: "barneveld", city: "Barneveld" },
                { slug: "breda", city: "Breda" },
                { slug: "waddinxveen", city: "Waddinxveen" },
                { slug: "zeewolde", city: "Zeewolde" },
              ].map((loc) => (
                <Link
                  key={loc.slug}
                  to={`/truckwash-${loc.slug}`}
                  data-testid={`location-strip-${loc.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0F0F23] px-4 py-2 text-sm text-white/75 hover:text-white hover:border-[#00D4FF]/40 hover:bg-[#101030] transition-colors"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#00D4FF]" />
                  <span>Truckwash {loc.city}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LAATSTE ARTIKELEN — internal linking for blog crawlability */}
      {latestPosts.length > 0 && (
        <section
          data-testid="home-latest-articles"
          className="py-24 sm:py-32"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
              <div>
                <p className="section-overline mb-3">{lang === "nl" ? "Kennisbank" : "Knowledge base"}</p>
                <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                  {lang === "nl" ? "Laatste artikelen." : "Latest articles."}
                </h2>
                <p className="mt-3 text-white/55 text-base max-w-xl">
                  {lang === "nl"
                    ? "Praktische inzichten over mobiele truckwash, HACCP, NVWA-controles en wagenparkbeheer."
                    : "Practical insights on mobile truckwash, HACCP, NVWA inspections and fleet management."}
                </p>
              </div>
              <Link
                to="/blog"
                data-testid="home-latest-articles-all"
                className="text-[#00D4FF] hover:text-white text-sm font-semibold inline-flex items-center gap-1"
              >
                {lang === "nl" ? "Bekijk alle artikelen" : "View all articles"} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {latestPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`home-latest-${p.slug}`}
                  className="group relative rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden card-hover flex flex-col"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={p.image}
                      alt={p[`title_${lang}`]}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded bg-[#050509]/70 backdrop-blur text-[10px] uppercase tracking-wider font-semibold text-[#00D4FF] border border-[#00D4FF]/30">
                      {p[`category_${lang}`]}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-base font-bold text-white tracking-tight mb-2 leading-snug line-clamp-2">{p[`title_${lang}`]}</h3>
                    <p className="text-white/55 text-sm leading-relaxed line-clamp-3 flex-1">{p[`excerpt_${lang}`]}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[#00D4FF] text-xs font-semibold">
                      {lang === "nl" ? "Lees artikel" : "Read article"}
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS / SOCIAL PROOF */}
      <GoogleReviews />

      {/* FAQ snippet */}
      {faqs.length > 0 && (
        <section className="py-24 sm:py-32 bg-[#0a0a18] border-y border-white/5">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="section-overline mb-3">FAQ</p>
              <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
                {lang === "nl" ? "Veelgestelde vragen" : "Frequently asked"}
              </h2>
            </div>
            <FAQAccordion items={faqs.slice(0, 5)} dataTestid="home-faq" />
            <div className="mt-10 text-center">
              <Link to="/faq" className="text-[#00D4FF] hover:text-white text-sm font-semibold inline-flex items-center gap-1">
                {lang === "nl" ? "Bekijk alle vragen" : "See all questions"} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </main>
  );
};

// helper - get image by slug
function services_image(slug) {
  const s = serviceList.find((x) => x.slug === slug);
  return s ? s.image : "";
}

export default Home;
